/**
 * Map browser language tags that don't appear in `availableLanguages`
 * to the closest code we ship. Applied per-candidate before matching.
 *
 * - `no` is the Norwegian macrolanguage; modern systems virtually always
 *   mean Bokmål in this context, and we only ship `nb`.
 */
const ALIASES: Record<string, string> = {
  no: 'nb',
};

function normalize(tag: string): string {
  if (!tag) return tag;
  const lower = tag.toLowerCase();
  if (ALIASES[lower]) return ALIASES[lower];
  const base = lower.split('-')[0];
  if (ALIASES[base]) return `${ALIASES[base]}${lower.slice(base.length)}`;
  return lower;
}

/**
 * Pick the best match for the user's preferred language list against the
 * locales we actually ship. Walks `candidates` in priority order, trying
 * each candidate as an exact match first, then its base language tag.
 *
 * `available` is the list of locale codes we ship (e.g. `['en', 'es', 'fr']`).
 * Returns the matched code, or `undefined` if nothing in `candidates` lines up.
 */
export function detectLocale(
  candidates: readonly (string | undefined | null)[],
  available: readonly string[],
): string | undefined {
  const set = new Set(available.map((c) => c.toLowerCase()));
  for (const raw of candidates) {
    if (!raw) continue;
    const candidate = normalize(raw);
    if (set.has(candidate)) return candidate;
    const base = candidate.split('-')[0];
    if (set.has(base)) return base;
  }
  return undefined;
}
